// apps/web/src/pages/DirectoryPage.tsx
import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import type { PublicProfile, DirectoryFilters } from '@hc-alumni/shared';
import {
  DIRECTORY_PAGE_SIZE_DESKTOP,
  DIRECTORY_PAGE_SIZE_MOBILE,
} from '@hc-alumni/shared/validation';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../hooks/useAuth';
import {
  Search,
  Filter,
  X,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Building,
  User,
  GraduationCap,
  MessageSquare,
  Briefcase,
  HandHelping,
} from 'lucide-react';

const GraduationCapIcon = GraduationCap;
const BuildingIcon = Building;
const MessageSquareIcon = MessageSquare;

interface DirectoryProfile extends PublicProfile {
  isPublic: boolean;
  industry?: string | null;
  lookingFor?: string | null;
}

function usePageSize() {
  const [limit, setLimit] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(min-width: 640px)').matches
      ? DIRECTORY_PAGE_SIZE_DESKTOP
      : DIRECTORY_PAGE_SIZE_MOBILE
  );

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 640px)');
    const apply = () =>
      setLimit(mq.matches ? DIRECTORY_PAGE_SIZE_DESKTOP : DIRECTORY_PAGE_SIZE_MOBILE);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  return limit;
}

export default function DirectoryPage() {
  const user = useAuth((s) => s.user);
  const authLoading = useAuth((s) => s.loading);
  const isGuest = !authLoading && !user?.id;
  const pageSize = usePageSize();

  const [filters, setFilters] = useState<DirectoryFilters>({
    q: '',
    department: '',
    year: undefined,
    company: '',
    industry: '',
    lookingFor: '',
    sort: 'name',
    order: 'asc',
    page: 1,
    limit: DIRECTORY_PAGE_SIZE_DESKTOP,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [allDepartments, setAllDepartments] = useState<string[]>([]);
  const [allYears, setAllYears] = useState<number[]>([]);

  // 螢幕尺寸變更時重設 limit 與 page
  useEffect(() => {
    setFilters((prev) => {
      if (prev.limit === pageSize) return prev;
      return { ...prev, limit: pageSize, page: 1 };
    });
  }, [pageSize]);

  const queryFilters = useMemo(
    () => ({ ...filters, limit: pageSize }),
    [filters, pageSize]
  );

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['directory', queryFilters],
    queryFn: () => api.directory.list(queryFilters),
    placeholderData: (prev) => prev,
  });

  useEffect(() => {
    if (data?.items) {
      const depts = new Set<string>();
      const years = new Set<number>();
      data.items.forEach((item) => {
        if (item.department) depts.add(item.department);
        if (item.graduateYear) years.add(item.graduateYear);
      });
      // 合併累積科系／年份選項
      setAllDepartments((prev) => Array.from(new Set([...prev, ...depts])).sort());
      setAllYears((prev) =>
        Array.from(new Set([...prev, ...years])).sort((a, b) => b - a)
      );
    }
  }, [data]);

  const items = (data?.items ?? []) as DirectoryProfile[];
  const total = data?.total ?? 0;
  const limit = data?.limit ?? pageSize;
  const page = data?.page ?? filters.page;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters((prev) => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      q: '',
      department: '',
      year: undefined,
      company: '',
      industry: '',
      lookingFor: '',
      sort: 'name',
      order: 'asc',
      page: 1,
      limit: pageSize,
    });
  };

  const hasActiveFilters =
    filters.q ||
    filters.department ||
    filters.year ||
    filters.company ||
    filters.industry ||
    filters.lookingFor;

  const goPage = (p: number) => {
    const next = Math.min(Math.max(1, p), totalPages);
    setFilters((prev) => ({ ...prev, page: next }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="page-shell">
      <section className="section-band mb-6 sm:mb-8 animate-fade-in">
        <div className="absolute inset-0 opacity-30 bg-[url('/cover-default.jpg')] bg-cover bg-center mix-blend-overlay" />
        <div className="relative px-5 py-8 sm:px-8 sm:py-10 lg:px-10 lg:py-12">
          <p className="text-alumni-100/90 text-xs sm:text-sm font-semibold tracking-[0.18em] uppercase mb-2">
            Fortune Institute · Alumni
          </p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">校友名冊</h1>
          <p className="mt-2 sm:mt-3 max-w-xl text-sm sm:text-base text-white/90 leading-relaxed">
            公開校友名片 · 目前共{' '}
            <span className="font-bold text-white tabular-nums">{total.toLocaleString()}</span> 位
            <span className="hidden sm:inline"> — 無需登入即可瀏覽，登入後可管理自己的名片</span>
          </p>
        </div>
      </section>

      {isGuest && (
        <div className="mb-4 sm:mb-6 rounded-2xl border border-[#06C755]/25 bg-gradient-to-r from-[#06C755]/10 to-alumni-50/80 dark:from-[#06C755]/15 dark:to-alumni-900/20 px-4 py-3.5 flex flex-col sm:flex-row sm:items-center gap-3 animate-slide-up">
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base">
              和春人回來掛個號吧
            </p>
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
              用 LINE 登入建立名片，審核後會出現在名冊
            </p>
          </div>
          <Link
            to="/login"
            className="inline-flex items-center justify-center gap-2 shrink-0 rounded-xl bg-[#06C755] px-4 py-2.5 text-sm font-semibold text-white shadow-soft active:scale-[0.98]"
          >
            加入校友會
          </Link>
        </div>
      )}

      <div className="card-glass mb-6 sm:mb-8 animate-slide-up">
        <form onSubmit={handleSearch} className="p-4 sm:p-6 space-y-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            <input
              type="search"
              value={filters.q || ''}
              onChange={(e) => setFilters((prev) => ({ ...prev, q: e.target.value, page: 1 }))}
              placeholder="搜尋姓名、公司、職稱、產業、產品、需求…"
              className="input pl-12 h-12 text-base"
              autoFocus
            />
            {filters.q && (
              <button
                type="button"
                onClick={() => setFilters((prev) => ({ ...prev, q: '', page: 1 }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800"
                aria-label="清除搜尋"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2 px-1">
            可搜尋姓名、公司、產業、產品或需求關鍵字
          </p>

          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className="btn-secondary btn-sm flex items-center gap-2 w-full sm:w-auto"
          >
            <Filter className="w-4 h-4" />
            進階篩選{' '}
            {hasActiveFilters && (
              <span className="badge badge-green">
                {
                  [
                    filters.q,
                    filters.department,
                    filters.year,
                    filters.company,
                    filters.industry,
                    filters.lookingFor,
                  ].filter(Boolean).length
                }
              </span>
            )}
            <ChevronDown
              className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`}
            />
          </button>

          {showFilters && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 animate-slide-down border-t border-gray-100 dark:border-gray-700 pt-4">
              <select
                value={filters.department || ''}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    department: e.target.value || '',
                    page: 1,
                  }))
                }
                className="input"
              >
                <option value="">所有科系</option>
                {allDepartments.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>

              <select
                value={filters.year?.toString() || ''}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    year: e.target.value ? parseInt(e.target.value, 10) : undefined,
                    page: 1,
                  }))
                }
                className="input"
              >
                <option value="">所有年份</option>
                {allYears.map((y) => (
                  <option key={y} value={y.toString()}>
                    西元 {y}／民國 {y - 1911}
                  </option>
                ))}
              </select>

              <input
                type="search"
                value={filters.company || ''}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, company: e.target.value, page: 1 }))
                }
                placeholder="公司關鍵字"
                className="input"
              />

              <input
                type="search"
                value={filters.industry || ''}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, industry: e.target.value, page: 1 }))
                }
                placeholder="產業／產品關鍵字"
                className="input"
              />

              <input
                type="search"
                value={filters.lookingFor || ''}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, lookingFor: e.target.value, page: 1 }))
                }
                placeholder="需求／尋求資源關鍵字"
                className="input"
              />

              <div className="flex items-end">
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="btn-ghost btn-sm w-full sm:w-auto"
                  >
                    <X className="w-4 h-4 mr-1" />
                    清除篩選
                  </button>
                )}
              </div>
            </div>
          )}
        </form>
      </div>

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {[...Array(pageSize)].map((_, i) => (
            <ProfileCardSkeleton key={i} />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 animate-fade-in">
          <User className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            找不到符合的校友
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            嘗試調整搜尋條件或清除篩選
          </p>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="btn-outline">
              <X className="w-4 h-4 mr-1" />
              清除所有篩選
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-3 text-sm text-gray-500 dark:text-gray-400">
            <span>
              第 <span className="font-semibold text-gray-700 dark:text-gray-200">{page}</span> /{' '}
              {totalPages} 頁
              <span className="hidden sm:inline"> · 每頁 {limit} 位</span>
            </span>
            {isFetching && (
              <span className="inline-flex items-center gap-1 text-xs">
                <LoadingSpinner className="w-3.5 h-3.5" /> 更新中
              </span>
            )}
          </div>

          <div className="grid gap-4 sm:gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {items.map((item) => (
              <ProfileCard key={item.id} profile={item} />
            ))}
          </div>

          {totalPages > 1 && (
            <nav
              className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4"
              aria-label="名冊分頁"
            >
              <p className="text-sm text-gray-500 dark:text-gray-400 order-2 sm:order-1">
                顯示第 {(page - 1) * limit + 1}–{Math.min(page * limit, total)} 位，共 {total} 位
              </p>
              <div className="flex items-center gap-2 order-1 sm:order-2">
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  disabled={page <= 1}
                  onClick={() => goPage(page - 1)}
                >
                  <ChevronLeft className="w-4 h-4 mr-0.5" />
                  上一頁
                </button>
                <div className="hidden sm:flex items-center gap-1">
                  {pageButtons(page, totalPages).map((p, i) =>
                    p === '…' ? (
                      <span key={`e-${i}`} className="px-1 text-gray-400">
                        …
                      </span>
                    ) : (
                      <button
                        key={p}
                        type="button"
                        onClick={() => goPage(p as number)}
                        className={`min-w-[2.25rem] h-9 rounded-lg text-sm font-medium transition-colors ${
                          p === page
                            ? 'bg-alumni-600 text-white shadow-soft'
                            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 hover:border-alumni-400'
                        }`}
                      >
                        {p}
                      </button>
                    )
                  )}
                </div>
                <span className="sm:hidden text-sm tabular-nums text-gray-600 dark:text-gray-300 px-1">
                  {page} / {totalPages}
                </span>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  disabled={page >= totalPages}
                  onClick={() => goPage(page + 1)}
                >
                  下一頁
                  <ChevronRight className="w-4 h-4 ml-0.5" />
                </button>
              </div>
            </nav>
          )}
        </>
      )}
    </div>
  );
}

/** 產生分頁按鈕（含省略） */
function pageButtons(current: number, total: number): Array<number | '…'> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set<number>([1, total, current, current - 1, current + 1]);
  if (current <= 3) [2, 3, 4].forEach((n) => pages.add(n));
  if (current >= total - 2) [total - 3, total - 2, total - 1].forEach((n) => pages.add(n));
  const sorted = Array.from(pages)
    .filter((n) => n >= 1 && n <= total)
    .sort((a, b) => a - b);
  const out: Array<number | '…'> = [];
  let prev = 0;
  for (const n of sorted) {
    if (prev && n - prev > 1) out.push('…');
    out.push(n);
    prev = n;
  }
  return out;
}

function charLen(s: string) {
  return Array.from(s).length;
}

function shouldStackCompanyTitle(company: string, title: string) {
  if (!company || !title) return false;
  const t = charLen(title);
  const c = charLen(company);
  return t >= 3 || c + t > 12;
}

function CompanyTitleLines({
  company,
  title,
  forceStack,
}: {
  company?: string | null;
  title?: string | null;
  forceStack?: boolean;
}) {
  const c = (company || '').trim();
  const t = (title || '').trim();
  if (!c && !t) return null;

  const stack = forceStack || shouldStackCompanyTitle(c, t);

  if (!c) {
    return <span className="block break-words">{t}</span>;
  }
  if (!t) {
    return <span className="block break-words">{c}</span>;
  }
  if (stack) {
    return (
      <>
        <span className="block break-words">{c}</span>
        <span className="mt-0.5 block break-words text-gray-500 dark:text-gray-400">{t}</span>
      </>
    );
  }
  return (
    <span className="block break-words">
      {c}
      <span className="text-gray-500 dark:text-gray-400"> · {t}</span>
    </span>
  );
}

function ProfileCard({ profile }: { profile: DirectoryProfile }) {
  const roc = profile.graduateYear != null ? profile.graduateYear - 1911 : null;

  return (
    <article className="card-hover group animate-fade-in overflow-hidden">
      <Link to={`/directory/${profile.id}`} className="block h-full">
        <div className="flex flex-row sm:flex-col h-full">
          <div className="relative w-[38%] min-w-[7.5rem] max-w-[11rem] sm:w-full sm:max-w-none sm:min-w-0 aspect-square sm:aspect-[4/3] shrink-0 overflow-hidden bg-gradient-to-br from-alumni-100 to-alumni-50 dark:from-gray-800 dark:to-gray-900">
            {profile.avatarUrl ? (
              <img
                src={profile.avatarUrl}
                alt=""
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                referrerPolicy="no-referrer"
                loading="lazy"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-hero-band">
                <span className="text-4xl sm:text-5xl font-bold text-white/95">
                  {profile.name.charAt(0)}
                </span>
              </div>
            )}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 sm:h-16 bg-gradient-to-t from-black/35 to-transparent" />
            {profile.isPublic ? (
              <span className="absolute top-2 right-2 sm:top-3 sm:right-3 badge badge-green shadow-soft backdrop-blur-sm text-[10px] sm:text-xs">
                公開
              </span>
            ) : null}
          </div>

          <div className="flex flex-1 flex-col justify-center gap-1.5 p-3.5 sm:p-5 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <h3 className="truncate text-base sm:text-base font-bold text-gray-900 dark:text-white">
                {profile.name}
              </h3>
              {profile.isFaculty ? (
                <span className="shrink-0 text-[10px] sm:text-xs font-semibold px-1.5 py-0.5 rounded-md bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200 border border-indigo-100 dark:border-indigo-800">
                  師長
                </span>
              ) : null}
            </div>

            {(profile.department || profile.graduateYear != null || profile.isFaculty) && (
              <p className="flex items-start gap-1.5 text-sm text-gray-600 dark:text-gray-400">
                <GraduationCapIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-alumni-600" />
                <span className="min-w-0 leading-snug break-words">
                  {profile.department && (
                    <span className="block sm:inline">{profile.department}</span>
                  )}
                  {profile.graduateYear != null && (
                    <span className="block sm:inline text-gray-500 dark:text-gray-400">
                      {profile.department ? (
                        <span className="hidden sm:inline"> · </span>
                      ) : null}
                      {profile.graduateYear}
                      {roc != null ? `（民${roc}）` : ''}
                    </span>
                  )}
                  {profile.isFaculty && !profile.department && profile.graduateYear == null && (
                    <span className="text-gray-500">師長／教職員</span>
                  )}
                </span>
              </p>
            )}

            {(profile.currentCompany || profile.currentTitle) && (
              <p className="flex items-start gap-1.5 text-sm text-gray-600 dark:text-gray-400">
                <BuildingIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-alumni-600" />
                <span className="min-w-0 leading-snug sm:hidden">
                  <CompanyTitleLines
                    company={profile.currentCompany}
                    title={profile.currentTitle}
                    forceStack
                  />
                </span>
                <span className="min-w-0 leading-snug hidden sm:block">
                  <CompanyTitleLines
                    company={profile.currentCompany}
                    title={profile.currentTitle}
                  />
                </span>
              </p>
            )}

            {profile.industry && (
              <p className="flex items-start gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                <Briefcase className="mt-0.5 h-3.5 w-3.5 shrink-0 text-alumni-500" />
                <span className="line-clamp-1">{profile.industry}</span>
              </p>
            )}

            {profile.lookingFor && (
              <p className="flex items-start gap-1.5 text-xs text-amber-700/90 dark:text-amber-300/90">
                <HandHelping className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span className="line-clamp-1">需要：{profile.lookingFor}</span>
              </p>
            )}

            {profile.lineId && (
              <span
                className="mt-0.5 inline-flex w-fit items-center gap-1.5 rounded-lg bg-[#06C755]/10 px-2 py-1 text-xs font-semibold text-[#05a84a]"
                onClick={(e) => e.preventDefault()}
              >
                <MessageSquareIcon className="h-3.5 w-3.5" />
                LINE
              </span>
            )}
          </div>
        </div>
      </Link>
    </article>
  );
}

function ProfileCardSkeleton() {
  return (
    <article className="card p-0 overflow-hidden animate-pulse">
      <div className="aspect-square bg-gray-200 dark:bg-gray-700" />
      <div className="p-4 space-y-3">
        <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
      </div>
    </article>
  );
}
